import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Loader2, Download, Calendar as CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format, parseISO } from "date-fns";
import { STATUSES, statusLabel } from "./constants";

export function AppDialog({ app, tailoredCvs, onSaved }) {
  const isEdit = !!app;
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fetching, setFetching] = useState(false);
  const blank = { company: "", job_title: "", job_url: "", job_description: "", status: "applied", date_applied: "", tailored_cv_id: "", notes: "" };
  const [form, setForm] = useState(app || blank);

  useEffect(() => {
    if (open) setForm(app ? { ...blank, ...app, tailored_cv_id: app.tailored_cv_id || "" } : blank);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const fetchFromUrl = async () => {
    if (!form.job_url || !form.job_url.trim()) { toast.error("Enter a job URL first"); return; }
    setFetching(true);
    try {
      const { data } = await api.post("/jobs/fetch-url", { url: form.job_url.trim() });
      setForm((f) => ({
        ...f,
        job_description: data.job_description || f.job_description,
        job_title: (!f.job_title || !f.job_title.trim()) && data.job_title ? data.job_title : f.job_title,
        company: (!f.company || !f.company.trim()) && data.company ? data.company : f.company,
      }));
      toast.success("Fetched job description from URL");
    } catch (e) {
      toast.error(formatApiError(e, "Could not fetch the job description"));
    } finally {
      setFetching(false);
    }
  };

  const save = async () => {
    if (!form.company.trim() || !form.job_title.trim()) { toast.error("Company and role are required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, tailored_cv_id: form.tailored_cv_id || null };
      if (isEdit) await api.put(`/applications/${app.id}`, payload);
      else await api.post("/applications", payload);
      toast.success(isEdit ? "Updated" : "Added");
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error("Could not save application");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {isEdit ? (
          <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`edit-app-${app.id}`}><Pencil className="h-4 w-4" strokeWidth={1.5} /></Button>
        ) : (
          <Button data-testid="add-application-button" className="active:scale-[0.98] transition-transform"><Plus className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Add Application</Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader><DialogTitle className="font-heading">{isEdit ? "Edit" : "New"} Application</DialogTitle><DialogDescription>Enter the application details.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-2">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Company</Label>
            <Input value={form.company} onChange={(e) => set("company", e.target.value)} data-testid="app-company-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Role</Label>
            <Input value={form.job_title} onChange={(e) => set("job_title", e.target.value)} data-testid="app-role-input" />
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Job URL</Label>
            <div className="flex gap-2">
              <Input value={form.job_url || ""} onChange={(e) => set("job_url", e.target.value)} placeholder="https://…" data-testid="app-url-input" />
              <Button type="button" variant="outline" onClick={fetchFromUrl} disabled={fetching} className="shrink-0" data-testid="app-fetch-url-button">
                {fetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                <span className="ml-1.5 hidden sm:inline">Fetch</span>
              </Button>
            </div>
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Job Description</Label>
            <Textarea rows={4} value={form.job_description || ""} onChange={(e) => set("job_description", e.target.value)} placeholder="Paste the job description (reused on the Screening page)…" data-testid="app-jobdesc-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Date Applied</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start font-normal" data-testid="app-date-input">
                  <CalendarIcon className="h-4 w-4 mr-2" strokeWidth={1.5} />
                  {form.date_applied ? format(parseISO(form.date_applied), "PP") : <span className="text-muted-foreground">Pick a date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={form.date_applied ? parseISO(form.date_applied) : undefined}
                  onSelect={(d) => set("date_applied", d ? format(d, "yyyy-MM-dd") : "")}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Status</Label>
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger data-testid="app-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => <SelectItem key={s} value={s}>{statusLabel(s)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Tailored CV</Label>
            <Select value={form.tailored_cv_id || "none"} onValueChange={(v) => set("tailored_cv_id", v === "none" ? "" : v)}>
              <SelectTrigger data-testid="app-tailoredcv-select"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {tailoredCvs.map((t) => <SelectItem key={t.id} value={t.id}>{t.job_title} · {t.company}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Notes</Label>
            <Textarea rows={3} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} data-testid="app-notes-input" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} data-testid="save-application-button" className="active:scale-[0.98] transition-transform">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
